import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { ToastService } from '../toast/toast.service';
import { LocationData } from './model/locationData';
import { APP_CONFIG } from '../../../config/app.config';
import { LABELS } from '../../../config/labels.config';
import { MapElementService } from './map-element.service';
import { Platform, ToastController } from '@ionic/angular';

declare var google;
declare let navigator: any;
declare let cordova: any;

@Injectable({
  providedIn: 'root'
})
export class MapService {

  interventionResume: boolean;

  constructor(
    public http: HttpClient,
    private logger: NGXLogger,
    private toastService: ToastService,
    private mapElementsService: MapElementService,
    private platform: Platform,
    private toastController: ToastController) {
    this.logger.debug('Hello MapProvider Provider');
  }

  getLocationFromPosition(location): Promise<LocationData> {
    return new Promise<LocationData>((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ 'location': location }, (results, status) => {
        if (status === 'OK') {
          if (results[0]) {
            const arrAddress = results[0].address_components;
            let name = '';
            let vicinity = '';
            let codePostal = '';
            let country = '';
            const lat = location.lat();
            const lng = location.lng();
            for (const address of arrAddress) {
              if (address.types[0] === 'street_number') {
                name = address.long_name;
              }
              if (address.types[0] === 'route') {
                name = name + ' ' + address.long_name;
              }
              if (address.types[0] === 'locality') {
                vicinity = address.long_name;
              }
              if (address.types[0] === 'postal_code') {
                codePostal = address.long_name;
              }
              if (address.types[0] === 'country'){
                country = address.long_name;
              }
            }
            if (!codePostal && lat && lng) {
              this.getPostalCodeFromLatLng(lat, lng)
                .then(cp => {
                  resolve(this.buildLocationData(lat, lng, name, vicinity, cp, country));
                })
                .catch(err => {
                  this.logger.debug(err);
                });
            } else {
              resolve(this.buildLocationData(lat, lng, name, vicinity, codePostal, country));
            }

          } else {
            reject('No results found');
            this.logger.debug('No results found');
          }
        } else {
          reject('Geocoder failed due to: ' + status);
          this.logger.debug('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  getUserLocation() {
    return new Promise<GeolocationPosition>((resolve, reject) => {
  
      navigator.geolocation.getCurrentPosition(
        (position) => { // Success callback
          this.logger.debug('getUserLocation() success getting position');
          this.setLastCalculatedPos(position);
          resolve(position);
        },
        (error) => { // Error callback
          this.logger.error('Error calculating the position: ' + error.code, error.message);
          
          // Handle the error
          if (this.platform.is('cordova')) {
            const successCallback = (isAvailable) => { 
              this.logger.debug('Is GPS available? ' + isAvailable); 
              if (isAvailable) {
                this.logger.debug('GPS Activé !!!');
                resolve(this.getDefaultPosObj());
              } else {
                this.logger.debug('GPS Désactivé !!!');
                this.presentGPSToast(
                  LABELS.app_toast_activate_localisation_head,
                  LABELS.app_toast_activate_localisation_message,
                  'water',
                  () => {
                    if (this.platform.is('android')) {
                      cordova.plugins.diagnostic.switchToLocationSettings();
                    } else {
                      cordova.plugins.diagnostic.switchToSettings();
                    }
                  }
                );
                resolve(this.getDefaultPosObj());
              }
            };
  
            const errorCallback = (e) => {
              this.presentGPSToast(
                LABELS.app_toast_activate_localisation_head,
                LABELS.app_toast_activate_localisation_message,
                'water',
                () => {
                  if (this.platform.is('android')) {
                    cordova.plugins.diagnostic.switchToLocationSettings();
                  } else {
                    cordova.plugins.diagnostic.switchToSettings();
                  }
                }
              );
              resolve(this.getDefaultPosObj());
            };
  
            cordova.plugins.diagnostic.isGpsLocationEnabled()
              .then(successCallback)
              .catch(errorCallback);
  
          } else { // If not Cordova
            if (error.code === 1 || error.message === 'User denied Geolocation') {
              this.logger.debug('User denied Geolocation');
              this.toastService.presentToast(LABELS.map_toast_geolocation_denied, APP_CONFIG.TOAST_TYPE_ALERT, { position: 'middle' });
              resolve(this.getDefaultPosObj());
            } else {
              if (this.getLastCalculatedPos() !== undefined) {
                this.logger.debug('Returning last calculated position.');
                resolve(this.getLastCalculatedPos());
              } else {
                this.logger.debug('Returning default position.');
                resolve(this.getDefaultPosObj());
              }
  
              this.toastService.presentToast(LABELS.map_toast_geolocation_not_calculated, APP_CONFIG.TOAST_TYPE_ALERT, { position: 'middle' });
            }
          }
        },
        APP_CONFIG.GEOLOC_DEFAULT_OPTIONS
      );
  
    });
  }
  

  async presentGPSToast(header, message, icon, callback) {
    const toast = await this.toastController.create({
      header,
      message,
      position: 'top',
      duration: 5000,
      buttons: [
        {
          side: 'start',
          icon,
        },
        {
          text: 'Activer',
          handler: () => {
            callback(); 
          }
        }
      ]
    });
    toast.present();
    toast.onDidDismiss().then(()=>{this.logger.debug('GPS toast dismissed')});
  }

  async getLocationFromPlace(place): Promise<LocationData> {
    const arrAddress = place.address_components;
    let name = '';
    let vicinity = '';
    let codePostal: any = '';
    let country = '';
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    for (const address of arrAddress) {
      if (address.types[0] === 'street_number') {
        name = address.long_name;
      }
      if (address.types[0] === 'route') {
        name = name + ' ' + address.long_name;
      }
      if (address.types[0] === 'locality') {
        vicinity = address.long_name;
      }
      if (address.types[0] === 'postal_code') {
        codePostal = address.long_name;
      }
      if (address.types[0] === 'country'){
        country = address.long_name;
      }
    }

    if (!codePostal && lat && lng) {
      codePostal = await this.getPostalCodeFromLatLng(lat, lng);
    }

    return (this.buildLocationData(lat, lng, name, vicinity, codePostal, country));
  }

  hasInterventionResume() {
    return this.interventionResume;
  }

  setInterventionResume(interventionResume) {
    this.interventionResume = interventionResume;
  }

  private getPostalCodeFromLatLng(lat, lng) {
    return new Promise<string>((resolve) => {
      const geocoder = new google.maps.Geocoder();
      const latlng = new google.maps.LatLng(lat, lng);
      let postalCode = '';
      geocoder.geocode({ 'latLng': latlng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0]) {
            const elt = results[0].address_components;
            elt.forEach(element => {
              if (element.types[0] === 'postal_code') {
                postalCode = element.long_name;
              }
            });
            resolve(postalCode);
          }
        } else {
          this.logger.error('Geocoder failed due to: ' + status);
          resolve('Failed');
        }
      });
    });
  }

  private buildLocationData(latitude, longitude, name, vicinity, codePostal, country): LocationData {
    const locationData = new LocationData();
    locationData.latitude = latitude;
    locationData.longitude = longitude;
    locationData.name = name;
    locationData.vicinity = vicinity;
    locationData.codePostal = codePostal;
    locationData.country = country;
    return locationData;
  }

  // paris
  private getDefaultPosObj(): GeolocationPosition {
    return {
      coords: {
        accuracy: undefined,
        altitude: undefined,
        altitudeAccuracy: undefined,
        heading: undefined,
        latitude: 48.8566,
        longitude: 2.3522,
        speed: undefined,
      },
      timestamp: undefined,
    };
  }

  private getLastCalculatedPos() {
    return this.mapElementsService.getLastCalculatedPos();
  }

  private setLastCalculatedPos(position) {
    this.mapElementsService.setLastCalculatedPos(position);
  }

}
